import React, { useState, useEffect } from "react";

import {
  Typography,
  TextField,
  DocumentList,
} from "@king-ict/eupisi-ui/components";
import {
  DocumentOriginEnum,
  RequestDocumentData,
} from "@king-ict/eupisi-ui/models";
import { hrDate, isEmpty } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  getRequestBasicInfo as getRequestBasicInfoApi,
  getApplicantDocument as getApplicantDocumentApi,
  getChildDocument as getChildDocumentApi,
  getOtherParentDocument as getOtherParentDocumentApi,
} from "../../../../api";
import { RequestBasicInfo, ParentNumberEnum } from "../../../../models";
import styles from "./RequestBasicInfoPage.module.scss";

export const RequestBasicInfoPage = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const [requestBasicInfo, setRequestBasicInfo] = useState<RequestBasicInfo>(
    {} as RequestBasicInfo
  );

  useEffect(() => {
    (async () => {
      if (requestId && isEmpty(requestBasicInfo)) {
        const requestBasicInfoData = await getRequestBasicInfoApi(+requestId);
        setRequestBasicInfo(requestBasicInfoData);
      }
    })();
  }, [requestId, requestBasicInfo]);

  const handleDownloadDocument = (document: RequestDocumentData) => {
    if (document.fileID) {
      document.origin === DocumentOriginEnum.OtherParent
        ? getOtherParentDocumentApi(document.fileID)
        : document.origin === DocumentOriginEnum.ApplicantParent
        ? getApplicantDocumentApi(document.fileID)
        : getChildDocumentApi(document.fileID);
    }
  };

  const renderSiblings = () => {
    return requestBasicInfo?.child?.siblings &&
      requestBasicInfo.child.siblings.length > 0 ? (
      <>
        <Row className={styles.rowHeadingSpace}>
          <Col md={12}>
            <Typography variant="smallHeading">
              {t("person.childSiblings")}
            </Typography>
          </Col>
        </Row>
        {requestBasicInfo.child.siblings.map((sibling) => {
          return (
            <React.Fragment key={sibling.oib}>
              <Row className={styles.rowSpace}>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.firstName")}
                    helperText={t("common.birthRegistry")}
                    value={sibling.firstName || emptyValue}
                    plaintext
                    bold
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.lastName")}
                    helperText={t("common.birthRegistry")}
                    value={sibling.lastName || emptyValue}
                    plaintext
                    bold
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.oib")}
                    helperText={t("common.oibApi")}
                    value={sibling.oib || emptyValue}
                    plaintext
                    bold
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.dateOfBirth")}
                    helperText={t("common.birthRegistry")}
                    value={
                      sibling.dateOfBirth
                        ? hrDate(sibling.dateOfBirth)
                        : emptyValue
                    }
                    plaintext
                    bold
                  />
                </Col>
              </Row>
            </React.Fragment>
          );
        })}
      </>
    ) : null;
  };

  const getChild = () => {
    return (
      <>
        <Row>
          <Col md={12}>
            <Typography className={styles.rowHeadingSpace} variant="largeBold">
              {t("requestList.basicChildInfo")}
            </Typography>
          </Col>
        </Row>
        <Row className={styles.rowSpace}>
          <Col md={12}>
            <Row className={styles.rowHeadingSpace}>
              <Col md={10}>
                <Typography variant="smallHeading">
                  {t("person.personalInformation")}
                </Typography>
              </Col>
            </Row>
            <Row className={styles.rowSpace}>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.firstName")}
                  helperText={t("common.birthRegistry")}
                  value={requestBasicInfo.child?.firstName || emptyValue}
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.lastName")}
                  helperText={t("common.birthRegistry")}
                  value={requestBasicInfo.child?.lastName || emptyValue}
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.oib")}
                  helperText={t("common.oibApi")}
                  value={requestBasicInfo.child?.oib || emptyValue}
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.dateOfBirth")}
                  helperText={t("common.birthRegistry")}
                  value={
                    requestBasicInfo.child && requestBasicInfo.child.dateOfBirth
                      ? hrDate(requestBasicInfo.child?.dateOfBirth)
                      : emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
            </Row>
            <Row className={styles.rowSpace}>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.birthplace")}
                  helperText={t("common.birthRegistry")}
                  value={requestBasicInfo.child?.birthplaceName || emptyValue}
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.sex")}
                  helperText={t("common.birthRegistry")}
                  value={requestBasicInfo.child?.sexName || emptyValue}
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.countryBirth")}
                  helperText={t("common.birthRegistry")}
                  value={
                    requestBasicInfo.child?.countryOfBirthName || emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.citizenship")}
                  helperText={t("common.birthRegistry")}
                  value={requestBasicInfo.child?.citizenshipName || emptyValue}
                  plaintext
                  bold
                />
              </Col>
            </Row>
            <Row className={styles.rowSpace}>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.nationalMinority")}
                  value={
                    requestBasicInfo.child.nationalMinorityName || emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.nationality")}
                  value={requestBasicInfo.child.nationalityName || emptyValue}
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={t("person.parentNumber")}
                  value={requestBasicInfo.parentNumberName || emptyValue}
                  plaintext
                  bold
                />
              </Col>
            </Row>

            {renderSiblings()}

            <Row className={styles.rowHeadingSpace}>
              <Col md={12}>
                <Typography variant="smallHeading">
                  {t("person.permanentResidenceInformation")}
                </Typography>
              </Col>
            </Row>
            <Row className={styles.rowSpace}>
              <Col md={3}>
                <TextField
                  type="text"
                  label={`${t("person.permanentResidence")} - ${t(
                    "person.country"
                  )}`}
                  value={
                    requestBasicInfo.child?.permanentResidence?.countryName ||
                    emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={`${t("person.permanentResidence")} - ${t(
                    "person.municipality"
                  )}`}
                  value={
                    requestBasicInfo.child?.permanentResidence
                      ?.municipalityName || emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={`${t("person.permanentResidence")} - ${t(
                    "person.town"
                  )}`}
                  value={
                    requestBasicInfo.child?.permanentResidence?.townName ||
                    emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={`${t("person.permanentResidence")} - ${t(
                    "person.postalCode"
                  )}`}
                  value={
                    requestBasicInfo.child?.permanentResidence
                      ?.townPostalCode || emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
            </Row>
            <Row className={styles.rowSpace}>
              <Col md={3}>
                <TextField
                  type="text"
                  label={`${t("person.permanentResidence")} - ${t(
                    "person.street"
                  )}`}
                  value={
                    requestBasicInfo.child?.permanentResidence?.street ||
                    emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={`${t("person.permanentResidence")} - ${t(
                    "person.streetNumber"
                  )}`}
                  value={
                    requestBasicInfo.child?.permanentResidence?.streetNumber ||
                    emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={`${t("person.permanentResidence")} - ${t(
                    "person.streetNumberAddition"
                  )}`}
                  value={
                    requestBasicInfo.child?.permanentResidence
                      ?.streetNumberAddition || emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
              <Col md={3}>
                <TextField
                  type="text"
                  label={`${t("person.permanentResidence")} - ${t(
                    "person.dateFrom"
                  )}`}
                  value={
                    requestBasicInfo?.child?.permanentResidence?.dateFrom
                      ? hrDate(
                          requestBasicInfo.child.permanentResidence.dateFrom
                        )
                      : emptyValue
                  }
                  plaintext
                  bold
                />
              </Col>
            </Row>
            {requestBasicInfo.child?.temporaryResidence ? (
              <>
                <Row className={styles.rowHeadingSpace}>
                  <Col md={12}>
                    <Typography variant="smallHeading">
                      {t("person.temporaryResidenceInformation")}
                    </Typography>
                  </Col>
                </Row>
                <Row className={styles.rowSpace}>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={`${t("person.temporaryResidence")} - ${t(
                        "person.country"
                      )}`}
                      value={
                        requestBasicInfo.child?.temporaryResidence
                          ?.countryName || emptyValue
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={`${t("person.temporaryResidence")} - ${t(
                        "person.municipality"
                      )}`}
                      value={
                        requestBasicInfo.child?.temporaryResidence
                          ?.municipalityName || emptyValue
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={`${t("person.temporaryResidence")} - ${t(
                        "person.town"
                      )}`}
                      value={
                        requestBasicInfo.child?.temporaryResidence?.townName ||
                        emptyValue
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={`${t("person.temporaryResidence")} - ${t(
                        "person.postalCode"
                      )}`}
                      value={
                        requestBasicInfo.child?.temporaryResidence
                          ?.townPostalCode || emptyValue
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
                <Row className={styles.rowSpace}>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={`${t("person.temporaryResidence")} - ${t(
                        "person.street"
                      )}`}
                      value={
                        requestBasicInfo.child?.temporaryResidence?.street ||
                        emptyValue
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={`${t("person.temporaryResidence")} - ${t(
                        "person.streetNumber"
                      )}`}
                      value={
                        requestBasicInfo.child?.temporaryResidence
                          ?.streetNumber || emptyValue
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={`${t("person.temporaryResidence")} - ${t(
                        "person.streetNumberAddition"
                      )}`}
                      value={
                        requestBasicInfo.child?.temporaryResidence
                          ?.streetNumberAddition || emptyValue
                      }
                      plaintext
                      bold
                    />
                  </Col>
                  <Col md={3}>
                    <TextField
                      type="text"
                      label={`${t("person.temporaryResidence")} - ${t(
                        "person.dateFrom"
                      )}`}
                      value={
                        requestBasicInfo?.child?.temporaryResidence?.dateFrom
                          ? hrDate(
                              requestBasicInfo.child.temporaryResidence.dateFrom
                            )
                          : emptyValue
                      }
                      plaintext
                      bold
                    />
                  </Col>
                </Row>
              </>
            ) : null}
            <Row className={styles.rowHeadingSpace}>
              <Col md={12}>
                <Typography
                  variant="smallHeading"
                  className={styles.documentSpaceClass}
                >
                  {t("common.documentsAdded")}
                </Typography>
                {requestBasicInfo.child?.documents &&
                requestBasicInfo.child?.documents.length > 0 ? (
                  <DocumentList
                    onDocumentClick={handleDownloadDocument}
                    color="primary"
                    documents={requestBasicInfo.child?.documents}
                    fullWidth
                    removable={false}
                  />
                ) : (
                  <Typography variant="baseBold" color="base">
                    {t("common.noDocuments")}
                  </Typography>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </>
    );
  };

  const getParent = (parent: any, headingText: string) => {
    return (
      <>
        <Row>
          <Col md={12}>
            <Typography className={styles.rowHeadingSpace1} variant="largeBold">
              {headingText}
            </Typography>
          </Col>
        </Row>
        {parent?.isOibNotExistent === false && (
          <Row className={styles.rowSpace}>
            <Col md={12}>
              <Row className={styles.rowHeadingSpace}>
                <Col md={10}>
                  <Typography variant="smallHeading">
                    {t("person.personalInformation")}
                  </Typography>
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.firstName")}
                    helperText={t("common.birthRegistry")}
                    value={parent?.firstName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.lastName")}
                    helperText={t("common.birthRegistry")}
                    value={parent?.lastName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.oib")}
                    helperText={t("common.oibApi")}
                    value={parent?.oib || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.dateOfBirth")}
                    helperText={t("common.birthRegistry")}
                    value={
                      parent && parent.dateOfBirth
                        ? hrDate(parent?.dateOfBirth)
                        : emptyValue
                    }
                    plaintext
                    bold
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.birthplace")}
                    helperText={t("common.birthRegistry")}
                    value={parent?.birthplaceName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.sex")}
                    helperText={t("common.birthRegistry")}
                    value={parent?.sexName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.countryBirth")}
                    helperText={t("common.birthRegistry")}
                    value={parent?.countryOfBirthName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.citizenship")}
                    helperText={t("common.birthRegistry")}
                    value={parent?.citizenshipName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.nationalMinority")}
                    value={parent.nationalMinorityName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.nationality")}
                    value={parent.nationalityName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.profession")}
                    value={parent.proffesionName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.professionalQualification")}
                    value={parent.professionalQualificationName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.email")}
                    value={parent.email || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.phoneNumber")}
                    value={parent.phoneNumber || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={t("person.parentshipType")}
                    value={parent.parentshipTypeName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowHeadingSpace}>
                <Col md={12}>
                  <Typography variant="smallHeading">
                    {t("person.permanentResidenceInformation")}
                  </Typography>
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.country"
                    )}`}
                    value={
                      parent?.permanentResidence?.countryName || emptyValue
                    }
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.municipality"
                    )}`}
                    value={
                      parent?.permanentResidence?.municipalityName || emptyValue
                    }
                    plaintext
                    bold
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.town"
                    )}`}
                    value={parent?.permanentResidence?.townName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.postalCode"
                    )}`}
                    value={
                      parent?.permanentResidence?.townPostalCode || emptyValue
                    }
                    bold
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.street"
                    )}`}
                    value={parent?.permanentResidence?.street || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.streetNumber"
                    )}`}
                    value={
                      parent?.permanentResidence?.streetNumber || emptyValue
                    }
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.streetNumberAddition"
                    )}`}
                    value={
                      parent?.permanentResidence?.streetNumberAddition ||
                      emptyValue
                    }
                    bold
                    plaintext
                  />
                </Col>
                <Col md={3}>
                  <TextField
                    type="text"
                    label={`${t("person.permanentResidence")} - ${t(
                      "person.dateFrom"
                    )}`}
                    value={
                      parent?.permanentResidence?.dateFrom
                        ? hrDate(parent.permanentResidence.dateFrom)
                        : emptyValue
                    }
                    bold
                    plaintext
                  />
                </Col>
              </Row>
              {parent?.temporaryResidence ? (
                <>
                  <Row className={styles.rowHeadingSpace}>
                    <Col md={12}>
                      <Typography variant="smallHeading">
                        {t("person.temporaryResidenceInformation")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={3}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.country"
                        )}`}
                        value={
                          parent?.temporaryResidence?.countryName || emptyValue
                        }
                        bold
                        plaintext
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.municipality"
                        )}`}
                        value={
                          parent?.temporaryResidence?.municipalityName ||
                          emptyValue
                        }
                        bold
                        plaintext
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.town"
                        )}`}
                        value={
                          parent?.temporaryResidence?.townName || emptyValue
                        }
                        bold
                        plaintext
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.postalCode"
                        )}`}
                        value={
                          parent?.temporaryResidence?.townPostalCode ||
                          emptyValue
                        }
                        bold
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={3}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.street"
                        )}`}
                        value={parent?.temporaryResidence?.street || emptyValue}
                        bold
                        plaintext
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.streetNumber"
                        )}`}
                        value={
                          parent?.temporaryResidence?.streetNumber || emptyValue
                        }
                        bold
                        plaintext
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.streetNumberAddition"
                        )}`}
                        value={
                          parent?.temporaryResidence?.streetNumberAddition ||
                          emptyValue
                        }
                        bold
                        plaintext
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        type="text"
                        label={`${t("person.temporaryResidence")} - ${t(
                          "person.dateFrom"
                        )}`}
                        value={
                          parent?.temporaryResidence?.dateFrom
                            ? hrDate(parent.temporaryResidence.dateFrom)
                            : emptyValue
                        }
                        bold
                        plaintext
                      />
                    </Col>
                  </Row>
                </>
              ) : null}
              <Row className={styles.rowHeadingSpace}>
                <Col md={12}>
                  <Typography
                    variant="smallHeading"
                    className={styles.documentSpaceClass}
                  >
                    {t("common.documentsAdded")}
                  </Typography>
                  {parent?.documents && parent?.documents.length > 0 ? (
                    <DocumentList
                      onDocumentClick={handleDownloadDocument}
                      color="primary"
                      documents={parent?.documents}
                      fullWidth
                      removable={false}
                    />
                  ) : (
                    <Typography variant="baseBold" color="base">
                      {t("common.noDocuments")}
                    </Typography>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        )}
        {parent?.isOibNotExistent === true && (
          <Row className={styles.rowSpace}>
            <Col md={12}>
              <Row className={styles.rowHeadingSpace}>
                <Col md={10}>
                  <Typography variant="smallHeading">
                    {t("person.personalInformation")}
                  </Typography>
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.firstName")}
                    value={parent?.firstName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.lastName")}
                    value={parent?.lastName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.yearOfBirth")}
                    value={parent?.yearOfBirth || emptyValue}
                    plaintext
                    bold
                  />
                </Col>
                <Col md={3}></Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.documentNumber")}
                    value={`${parent?.documentNumber}.` || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.documentTypeId")}
                    value={parent?.documentTypeName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.reasonNonExistenceOibId")}
                    value={parent?.reasonNonExistenceOibName || emptyValue}
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={2}>
                  <TextField
                    type="text"
                    label={t("person.yearOfBirth")}
                    value={`${parent?.yearOfBirth}.` || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={2}>
                  <TextField
                    type="text"
                    label={t("person.sex")}
                    value={parent?.sexName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.profession")}
                    value={parent?.professionName || emptyValue}
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.professionalQualification")}
                    value={parent?.professionalQualificationName || emptyValue}
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.email")}
                    value={parent.email || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.phoneNumber")}
                    value={parent.phoneNumber || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.parentshipType")}
                    value={parent.parentshipTypeName || emptyValue}
                    bold
                    plaintext
                  />
                </Col>
              </Row>
              <Row className={styles.rowHeadingSpace}>
                <Col md={12}>
                  <Typography
                    variant="smallHeading"
                    className={styles.documentSpaceClass}
                  >
                    {t("common.documentsAdded")}
                  </Typography>
                  {parent?.documents && parent?.documents.length > 0 ? (
                    <DocumentList
                      onDocumentClick={handleDownloadDocument}
                      color="primary"
                      documents={parent?.documents}
                      fullWidth
                      removable={false}
                    />
                  ) : (
                    <Typography variant="baseBold" color="base">
                      {t("common.noDocuments")}
                    </Typography>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </>
    );
  };
  const { t } = useTranslation();
  const emptyValue = "-";
  return (
    <>
      {!isEmpty(requestBasicInfo) && getChild()}
      {!isEmpty(requestBasicInfo) &&
        getParent(
          requestBasicInfo.applicantParent,
          t("person.applicantPersonalInformation")
        )}
      {!isEmpty(requestBasicInfo) &&
        requestBasicInfo.parentNumber === ParentNumberEnum.TwoParents &&
        requestBasicInfo.otherParent &&
        getParent(
          requestBasicInfo.otherParent,
          t("person.otherPersonalInformation")
        )}
    </>
  );
};
